import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Card,
    CardContent,
    CircularProgress,
    Divider,
    LinearProgress,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useApplicationContext } from 'ApplicationContext';
import { settingsApi } from './settingsApi';

interface UsageMonth {
    month: string;
    calls: number;
    estimatedEur: number;
    perModel: Record<string, number>;
}

interface UsageSlice {
    name: string;
    calls: number;
    inputTokens: number;
    outputTokens: number;
    cachedTokens: number;
    cacheWriteTokens: number;
    estimatedEur: number;
    /** Anteil an den Gesamtkosten des Zeitraums, 0…1. */
    share: number;
}

interface UsageCurrent {
    month: string;
    drafts: number;
    calls: number;
    inputTokens: number;
    outputTokens: number;
    cachedTokens: number;
    cacheWriteTokens: number;
    estimatedEur: number;
    budgetEur: number;
    overBudget: boolean;
    nearBudget: boolean;
}

/**
 * Die tatsächliche Abrechnung des Anbieters.
 *
 * Getrennt von der Schätzung ausgewiesen und nicht mit ihr verrechnet: läuft die Schätzung von der
 * Abrechnung weg, ist entweder ein Aufruf nicht gebucht (dann fehlt Sichtbarkeit) oder ein Preis
 * veraltet (dann stimmt die Notbremse nicht mehr). Ohne den Vergleich merkt das niemand.
 */
interface ActualCost {
    totalUsd: number;
    totalEur: number;
    perMonthUsd: Record<string, number>;
    perModelUsd: Record<string, number>;
    perCostTypeUsd: Record<string, number>;
    fetchedAt: string | null;
    /** Schätzung geteilt durch Abrechnung: 1,0 = Übereinstimmung. */
    deviation: number | null;
}

/**
 * Ein aus der eigenen Abrechnung abgeleiteter Token-Preis.
 *
 * Eine Preisliste gibt es beim Anbieter nicht abzufragen. Betrag geteilt durch Menge ergibt aber den
 * Preis, den **wir** zahlen — einschließlich Konditionen, und immer aktuell, weil er sich mit jeder
 * Abrechnung selbst nachführt.
 */
interface DerivedPrice {
    model: string;
    tokenType: string;
    usdPerMillion: number;
    tokens: number;
    updatedAt: string;
    /** „Abgeleitet" (gemessen) oder „Eingestellt" (von Hand, überlebt den Abgleich). */
    source: string;
    setBy: string | null;
}

/** Der Kurs, mit dem umgerechnet wurde — er gehört sichtbar dazu. */
interface ExchangeRateDto {
    usdToEur: number;
    /** EZB, „EZB (zuletzt bekannt)", eingestellt oder Vorgabe. */
    source: string;
    asOf: string | null;
}

interface UsageReport {
    months: UsageMonth[];
    models: UsageSlice[];
    purposes: UsageSlice[];
    features: UsageSlice[];
    totalEur: number;
    current: UsageCurrent;
    modelNames: string[];
    actual: ActualCost | null;
    prices: DerivedPrice[] | null;
    rate: ExchangeRateDto | null;
}

/**
 * Kategoriale Farben in **fester** Reihenfolge, nie im Kreis vergeben.
 *
 * Beide Modi sind eigens gewählt und geprüft: schlechteste benachbarte Paare bei ΔE 9.1 (hell) und
 * 8.4 (dunkel) für Farbfehlsichtigkeit, 22.9 bzw. 19.8 für normales Sehen. Im Hellmodus liegen Aqua
 * und Gelb unter 3:1 Kontrast zur Fläche — deshalb ist die Tabelle darunter nicht schmückendes
 * Beiwerk, sondern die Pflichtentsprechung dazu.
 *
 * Die Zuordnung folgt der **Identität** des Modells, nicht seinem Rang. Ein Filter, der ein Modell
 * herausnimmt, darf die übrigen nicht umfärben.
 */
const SERIES_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const SERIES_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];

/** Zwecke in Klartext — „DokumentAuswertung" liest sich nicht. */
const PURPOSE_LABELS: Record<string, string> = {
    Dialog: 'Gespräch',
    Verdichtung: 'Verlauf verdichten',
    DokumentSichtung: 'Unterlage sichten',
    DokumentAuswertung: 'Unterlage auswerten',
    DokumentFrage: 'Im Dokument nachlesen',
    Frageerzeugung: 'Gesprächsfragen erzeugen',
    Vorlagenerzeugung: 'Vorlagen erzeugen',
    Verbindungstest: 'Verbindungstest',
    Sonstiges: 'Sonstiges',
    Uebernommen: 'Aus der früheren Zählung (grob)',
};

/** Die Token-Arten in der Reihenfolge, in der man sie einträgt. */
const TOKEN_TYPE_OPTIONS = [
    'uncached_input_tokens',
    'output_tokens',
    'cache_read_input_tokens',
    'cache_creation.ephemeral_5m_input_tokens',
    'cache_creation.ephemeral_1h_input_tokens',
];

/** Die Token-Arten des Anbieters in Klartext. */
const TOKEN_TYPE_LABELS: Record<string, string> = {
    uncached_input_tokens: 'Eingabe',
    output_tokens: 'Ausgabe',
    cache_read_input_tokens: 'Cache gelesen',
    'cache_creation.ephemeral_5m_input_tokens': 'Cache geschrieben (5 min)',
    'cache_creation.ephemeral_1h_input_tokens': 'Cache geschrieben (1 h)',
};

/** Die Kategorien des Anbieters in Klartext. */
const COST_TYPE_LABELS: Record<string, string> = {
    tokens: 'Token',
    web_search: 'Websuche',
    code_execution: 'Codeausführung',
    session_usage: 'Sitzungen',
    sonstiges: 'Sonstiges',
};

const usd = (v: number) =>
    v.toLocaleString('de-DE', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

const eur = (v: number) =>
    v.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });

const tokens = (v: number) => {
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} Mio.`;
    if (v >= 1_000) return `${Math.round(v / 1000)}k`;
    return String(v);
};

const monthLabel = (iso: string) =>
    new Date(iso).toLocaleDateString('de-DE', { month: 'short', year: '2-digit' });

/**
 * Die Kostenauswertung des KI-Assistenten.
 *
 * **Warum es das gibt.** „Wir zahlen zu viel" ist keine Erkenntnis, mit der sich etwas ändern lässt.
 * Erst die Aufteilung sagt, wo man ansetzt: ob es das Gespräch ist, das mit jedem Zug den ganzen
 * Verlauf mitschleppt, oder die Auswertung großer Unterlagen, oder ein Nachfülllauf, den jemand
 * zweimal angestoßen hat.
 *
 * Drei Ebenen, in dieser Reihenfolge: **wie viel** (Summe und laufender Monat gegen das Budget),
 * **wann** (die Zeitreihe — ein Sprung hat immer einen Anlass), **wofür und womit** (Zweck und
 * Modell). Die Zahlen sind eine Schätzung; das steht dabei, damit niemand sie für die Abrechnung
 * hält.
 */
export const AiCostReport: React.FC = () => {
    const theme = useTheme();
    const context = useApplicationContext();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const [months, setMonths] = useState(12);
    const [report, setReport] = useState<UsageReport | null>(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    /** Welche Preiszeile gerade bearbeitet wird, und mit welchem Wert. */
    const [editing, setEditing] = useState<{ key: string; value: string } | null>(null);
    const [savingPrice, setSavingPrice] = useState(false);

    /** Eingabe für einen ganz neuen Preis — der Weg ohne Admin-Schlüssel. */
    const [newPrice, setNewPrice] = useState({ model: '', tokenType: TOKEN_TYPE_OPTIONS[0], value: '' });
    const [syncNote, setSyncNote] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await http.get<UsageReport>(`/AiSettings/UsageReport?months=${months}`);
            setReport(r.data);
        } catch {
            setError('Die Kostenauswertung konnte nicht geladen werden.');
        } finally {
            setLoading(false);
        }
    }, [http, months]);

    useEffect(() => { load(); }, [load]);

    /**
     * Holt die Abrechnung beim Anbieter und lädt danach neu.
     *
     * Läuft ohnehin zweimal täglich im Hintergrund; der Knopf ist für den Moment, in dem jemand
     * gerade nachsehen will. Der Anbieter meldet Daten binnen etwa fünf Minuten nach einem Aufruf.
     */
    const sync = useCallback(async () => {
        setSyncing(true);
        setSyncNote(null);
        try {
            const r = await http.post<{ success: boolean; days: number; items: number; totalUsd: number; error: string | null }>(
                `/AiSettings/SyncCosts?months=${months}`);
            setSyncNote(r.data.success
                ? `${r.data.items} Posten an ${r.data.days} Tagen übernommen.`
                : r.data.error);
            if (r.data.success) await load();
        } catch {
            setSyncNote('Der Abgleich ist fehlgeschlagen.');
        } finally {
            setSyncing(false);
        }
    }, [http, months, load]);

    /**
     * Setzt einen Preis von Hand oder gibt ihn wieder frei.
     *
     * Ein gesetzter Preis hat Vorrang vor dem gemessenen und überlebt den nächsten Abgleich — sonst
     * trägt jemand einen ausgehandelten Satz ein und findet zwölf Stunden später still wieder den
     * gemessenen vor.
     */
    const savePrice = useCallback(async (model: string, tokenType: string, value: string) => {
        const zahl = Number(value.replace(',', '.'));
        if (!Number.isFinite(zahl) || zahl < 0) return;

        setSavingPrice(true);
        try {
            await http.put('/AiSettings/Price', { model, tokenType, usdPerMillion: zahl });
            setEditing(null);
            await load();
        } catch {
            setSyncNote('Der Preis konnte nicht gespeichert werden.');
        } finally {
            setSavingPrice(false);
        }
    }, [http, load]);

    const releasePrice = useCallback(async (model: string, tokenType: string) => {
        setSavingPrice(true);
        try {
            await http.delete(
                `/AiSettings/Price?model=${encodeURIComponent(model)}`
                + `&tokenType=${encodeURIComponent(tokenType)}`);
            await load();
        } catch {
            setSyncNote('Der Preis konnte nicht freigegeben werden.');
        } finally {
            setSavingPrice(false);
        }
    }, [http, load]);

    /**
     * Dollar in Euro, mit dem Kurs aus der Antwort.
     *
     * Nicht mit einem im Client verdrahteten Wert: der Kurs kommt vom Server, weil dort auch die
     * gespeicherten Schätzungen damit gerechnet wurden. Zwei Kurse in einer Ansicht ergäben zwei
     * Wahrheiten.
     */
    const toEur = useCallback(
        (dollar: number) => eur(dollar * (report?.rate?.usdToEur ?? 0)),
        [report],
    );

    const dark = theme.palette.mode === 'dark';
    const palette = dark ? SERIES_DARK : SERIES_LIGHT;
    const surface = theme.palette.background.paper;

    /*
     * Farbe je Modellname, nicht je Position in der gefilterten Liste. Sonst wechselt Opus die
     * Farbe, sobald ein Monat ohne Haiku dazukommt — und dann bedeutet Blau in zwei Ansichten
     * Verschiedenes.
     */
    const colorFor = useCallback(
        (name: string) => {
            const index = report?.modelNames.indexOf(name) ?? -1;
            return palette[(index < 0 ? 0 : index) % palette.length];
        },
        [report, palette],
    );

    const series = useMemo(() => {
        if (!report) return [];
        return report.modelNames.map((name) => ({
            data: report.months.map((m) => m.perModel[name] ?? 0),
            label: name,
            stack: 'kosten',
            color: colorFor(name),
            valueFormatter: (v: number | null) => (v === null ? '' : eur(v)),
        }));
    }, [report, colorFor]);

    if (loading && !report) {
        return (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', py: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Kostenauswertung wird geladen …</Typography>
            </Stack>
        );
    }

    if (error) return <Alert severity="warning">{error}</Alert>;
    if (!report) return null;

    const { current } = report;
    const budgetShare = current.budgetEur > 0
        ? Math.min(1, current.estimatedEur / current.budgetEur)
        : 0;

    const leer = report.months.every((m) => m.calls === 0);

    return (
        <Stack spacing={2}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>Kosten</Typography>
                <ToggleButtonGroup
                    size="small" exclusive value={months}
                    onChange={(_, v) => { if (v) setMonths(v); }}
                >
                    <ToggleButton value={3}>3 Monate</ToggleButton>
                    <ToggleButton value={12}>12 Monate</ToggleButton>
                    <ToggleButton value={24}>24 Monate</ToggleButton>
                </ToggleButtonGroup>
                <Button
                    size="small" variant="outlined" disabled={syncing}
                    onClick={sync}
                    startIcon={syncing ? <CircularProgress size={14} /> : undefined}
                >
                    Abrechnung holen
                </Button>
            </Stack>

            {syncNote && <Alert severity="info" onClose={() => setSyncNote(null)}>{syncNote}</Alert>}

            {/*
              * Der Kurs gehört sichtbar dazu: eine Euro-Zahl ohne den Kurs, der sie erzeugt hat,
              * lässt sich nicht nachrechnen — und genau das will jemand, der die Rechnung prüft.
              */}
            {report.rate && (
                <Typography variant="caption" color="text.secondary">
                    Umgerechnet mit <b>1 USD = {report.rate.usdToEur.toLocaleString('de-DE', {
                        minimumFractionDigits: 4, maximumFractionDigits: 6,
                    })} €</b>
                    {' · '}
                    {report.rate.source}
                    {report.rate.asOf
                        ? ` vom ${new Date(report.rate.asOf).toLocaleDateString('de-DE')}`
                        : ''}
                </Typography>
            )}

            {/*
              * Die Kennzahlen zuerst und groß. Wer diese Seite öffnet, will als Erstes wissen, ob
              * es viel ist — nicht, wie es sich verteilt.
              */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap
                sx={{ flexWrap: 'wrap' }}>
                {/*
                  * Die Abrechnung zuerst, wenn es sie gibt: sie ist die Zahl, über die sich mit der
                  * Buchhaltung reden lässt. Die eigene Schätzung steht daneben, nicht darüber.
                  */}
                {/*
                  * Ohne Admin-Schlüssel bleibt die abgerechnete Zahl LEER. Hier die Schätzung
                  * hinzuschreiben hieße, sie als das auszugeben, was sie nicht ist — und niemand
                  * würde je merken, dass der Schlüssel fehlt.
                  */}
                <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography variant="overline" color="text.secondary">
                            Abgerechnet ({months} Monate)
                        </Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                color: report.actual ? 'text.primary' : 'text.disabled',
                            }}
                        >
                            {report.actual ? usd(report.actual.totalUsd) : '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {report.actual
                                ? `≈ ${eur(report.actual.totalEur)}`
                                : 'Kein Admin-Schlüssel hinterlegt'}
                        </Typography>
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography variant="overline" color="text.secondary">
                            Geschätzt ({months} Monate)
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {eur(report.totalEur)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {report.months.reduce((s, m) => s + m.calls, 0).toLocaleString('de-DE')} Modellaufrufe
                        </Typography>
                    </CardContent>
                </Card>

                <Card variant="outlined" sx={{ flex: 1 }}>
                    <CardContent>
                        <Typography variant="overline" color="text.secondary">Laufender Monat</Typography>
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                color: current.overBudget ? 'error.main'
                                    : current.nearBudget ? 'warning.main' : 'text.primary',
                            }}
                        >
                            {eur(current.estimatedEur)}
                        </Typography>
                        {current.budgetEur > 0 ? (
                            <>
                                <LinearProgress
                                    variant="determinate"
                                    value={budgetShare * 100}
                                    color={current.overBudget ? 'error' : current.nearBudget ? 'warning' : 'primary'}
                                    sx={{ my: 0.75, height: 6, borderRadius: 3 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                    {Math.round(budgetShare * 100)} % von {eur(current.budgetEur)} ·{' '}
                                    {current.drafts} Gespräche
                                </Typography>
                            </>
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                Kein Budget hinterlegt · {current.drafts} Gespräche
                            </Typography>
                        )}
                    </CardContent>
                </Card>

                {/*
                  * Die Abweichung ist die Selbstkontrolle. Weicht die Schätzung stark ab, ist
                  * entweder ein Aufruf nicht gebucht — dann fehlt Sichtbarkeit — oder ein Preis
                  * veraltet, dann löst die Notbremse zur falschen Zeit aus.
                  */}
                {report.actual?.deviation != null && (
                    <Card variant="outlined" sx={{ flex: 1 }}>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary">
                                Schätzung gegen Abrechnung
                            </Typography>
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                                    color: Math.abs(report.actual.deviation - 1) > 0.25
                                        ? 'warning.main' : 'success.main',
                                }}
                            >
                                {`${Math.round(report.actual.deviation * 100)} %`}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {Math.abs(report.actual.deviation - 1) <= 0.25
                                    ? 'Die Schätzung trägt.'
                                    : report.actual.deviation < 1
                                        ? 'Zu niedrig geschätzt — es fehlen gebuchte Aufrufe oder ein Preis ist veraltet.'
                                        : 'Zu hoch geschätzt — vermutlich ist ein Preis in der Tabelle zu hoch.'}
                            </Typography>
                        </CardContent>
                    </Card>
                )}
            </Stack>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Kosten je Monat, nach Modell</Typography>
                    <Typography variant="caption" color="text.secondary">
                        Ein Sprung hat immer einen Anlass — meist ein Gespräch mit großen Unterlagen.
                    </Typography>

                    {leer ? (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Für diesen Zeitraum sind noch keine Aufrufe erfasst. Das Journal beginnt mit
                            der Einführung dieser Auswertung — frühere Monate lassen sich nicht
                            rekonstruieren, weil der Verbrauch damals nur je Gespräch summiert wurde.
                        </Alert>
                    ) : (
                        <Box sx={{ width: '100%', height: 320, mt: 1 }}>
                            <BarChart
                                height={320}
                                series={series}
                                xAxis={[{
                                    scaleType: 'band',
                                    data: report.months.map((m) => monthLabel(m.month)),
                                }]}
                                yAxis={[{ valueFormatter: (v: number) => eur(v) }]}
                                margin={{ left: 70, right: 12, top: 12, bottom: 24 }}
                                borderRadius={4}
                                slotProps={{ legend: { direction: 'horizontal', position: { vertical: 'bottom' } } }}
                                sx={{
                                    /*
                                     * 2 px in der Flächenfarbe zwischen den Segmenten: benachbarte
                                     * Stapelabschnitte lesen sich durch die Lücke als getrennt, nicht
                                     * durch einen Rahmen. Zugleich die zweite Kodierung neben der
                                     * Farbe, die für das Paar Gelb/Aqua verlangt ist.
                                     */
                                    '& .MuiBarElement-root': {
                                        stroke: surface,
                                        strokeWidth: 2,
                                    },
                                    // Zurückhaltendes Raster: es hilft beim Ablesen und tritt sonst zurück.
                                    '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
                                    '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': {
                                        stroke: theme.palette.divider,
                                    },
                                    '& .MuiChartsAxis-tickLabel': { fill: theme.palette.text.secondary },
                                }}
                                grid={{ horizontal: true }}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/*
              * Die eigentliche Antwort auf „warum ist das so teuer". Zweck vor Modell, weil man am
              * Zweck etwas ändern kann und am Modell nur die Rechnung.
              */}
            {/*
              * Die Abrechnung des Anbieters, nach seinen eigenen Kategorien. Sie enthält Posten, die
              * in der eigenen Zählung gar nicht vorkommen können — Websuche, Codeausführung —, und
              * genau deshalb steht sie hier eigenständig und nicht als Korrektur der Schätzung.
              */}
            {report.actual && (
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', mb: 1 }}>
                            <Typography variant="subtitle2">Abrechnung des Anbieters</Typography>
                            <Typography variant="caption" color="text.secondary">
                                {report.actual.fetchedAt
                                    ? `Stand ${new Date(report.actual.fetchedAt).toLocaleString('de-DE')}`
                                    : ''}
                            </Typography>
                        </Stack>

                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Art</TableCell>
                                    <TableCell align="right">USD</TableCell>
                                    <TableCell align="right">EUR</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Object.entries(report.actual.perCostTypeUsd)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([art, betrag]) => (
                                        <TableRow key={art} hover>
                                            <TableCell>{COST_TYPE_LABELS[art] ?? art}</TableCell>
                                            <TableCell align="right"
                                                sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                {usd(betrag)}
                                            </TableCell>
                                            <TableCell align="right"
                                                sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                                                {toEur(betrag)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>

                        <Divider sx={{ my: 1.5 }} />
                        <Typography variant="caption" color="text.secondary">
                            Kosten des Priority-Tarifs sind in dieser Schnittstelle nicht enthalten —
                            wer ihn nutzt, findet sie nur in der Konsole. Die Umrechnung in Euro
                            erfolgt mit einem festen angenommenen Kurs.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/*
              * Woher zuerst, dann wofür, dann womit. „Der Angebotsassistent kostet 80 %" ist die
              * Aussage, mit der ein Gespräch über Kosten anfängt; das Modell ist die letzte Frage,
              * weil man daran am wenigsten ändern kann.
              */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Woher</Typography>
                    <ShareTable rows={report.features} label={(n) => n}
                        accent={theme.palette.text.secondary} />
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Wofür</Typography>
                    <ShareTable rows={report.purposes} label={(n) => PURPOSE_LABELS[n] ?? n} accent={theme.palette.text.secondary} />
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Womit</Typography>
                    <ShareTable rows={report.models} label={(n) => n} color={colorFor} />

                    <Divider sx={{ my: 1.5 }} />
                    <Typography variant="caption" color="text.secondary">
                        Aus dem Zwischenspeicher gelesene Token kosten rund ein Zehntel, hineingeschriebene
                        rund das Anderthalbfache. Ein hoher Leseanteil ist gut; ein hoher Schreibanteil bei
                        wenig Leseanteil heißt, dass viele kurze Gespräche den Aufschlag immer wieder zahlen,
                        ohne je davon zu profitieren.
                    </Typography>
                </CardContent>
            </Card>

            {/*
              * Die Preise, mit denen gerechnet wird. Abgeleitet statt abgefragt: eine Preisliste
              * gibt es beim Anbieter nicht — die Models-API liefert Kontextfenster und Fähigkeiten,
              * aber keinen Preis. Betrag geteilt durch Menge ist ohnehin die bessere Zahl, weil sie
              * die eigenen Konditionen kennt.
              */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Was ein Token kostet
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Aus der eigenen Abrechnung abgeleitet: Betrag geteilt durch Menge. Das ist der
                        Preis, den <b>ihr</b> zahlt — einschließlich Konditionen, und er führt sich mit
                        jeder Abrechnung selbst nach. <b>Auf einen Preis klicken, um ihn von Hand zu
                        setzen</b>; ein gesetzter Preis hat Vorrang und überlebt jeden Abgleich, bis
                        ihn jemand wieder freigibt.
                    </Typography>

                    {report.prices && report.prices.length > 0 ? (
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Modell</TableCell>
                                    <TableCell>Token-Art</TableCell>
                                    <TableCell align="right">je Mio. (USD)</TableCell>
                                    <TableCell align="right">je Mio. (EUR)</TableCell>
                                    <TableCell align="right">Herkunft</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {report.prices.map((p) => {
                                    const key = `${p.model}|${p.tokenType}`;
                                    const eingestellt = p.source === 'Eingestellt';
                                    const bearbeitet = editing?.key === key;

                                    return (
                                        <TableRow key={key} hover>
                                            <TableCell>{p.model}</TableCell>
                                            <TableCell>{TOKEN_TYPE_LABELS[p.tokenType] ?? p.tokenType}</TableCell>
                                            <TableCell align="right">
                                                {bearbeitet ? (
                                                    <TextField
                                                        size="small" autoFocus type="number"
                                                        value={editing.value}
                                                        disabled={savingPrice}
                                                        onChange={(e) => setEditing({ key, value: e.target.value })}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                savePrice(p.model, p.tokenType, editing.value);
                                                            }
                                                            if (e.key === 'Escape') setEditing(null);
                                                        }}
                                                        slotProps={{ htmlInput: { step: 0.01, min: 0, style: { textAlign: 'right' } } }}
                                                        sx={{ width: 120 }}
                                                    />
                                                ) : (
                                                    <Box
                                                        component="span"
                                                        onClick={() => setEditing({
                                                            key, value: String(p.usdPerMillion),
                                                        })}
                                                        sx={{
                                                            fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                                                            cursor: 'pointer', borderBottom: '1px dashed',
                                                            borderColor: 'divider',
                                                        }}
                                                    >
                                                        {p.usdPerMillion.toLocaleString('de-DE', {
                                                            style: 'currency', currency: 'USD',
                                                            minimumFractionDigits: 2, maximumFractionDigits: 4,
                                                        })}
                                                    </Box>
                                                )}
                                            </TableCell>
                                            <TableCell align="right"
                                                sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                                                {toEur(p.usdPerMillion)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {bearbeitet ? (
                                                    <Stack direction="row" spacing={0.5} sx={{ justifyContent: 'flex-end' }}>
                                                        <Button size="small" disabled={savingPrice}
                                                            onClick={() => savePrice(p.model, p.tokenType, editing.value)}>
                                                            Setzen
                                                        </Button>
                                                        <Button size="small" color="inherit"
                                                            onClick={() => setEditing(null)}>
                                                            Abbrechen
                                                        </Button>
                                                    </Stack>
                                                ) : (
                                                    <Stack direction="row" spacing={1}
                                                        sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                                                        {eingestellt ? (
                                                            <>
                                                                <Tooltip title={p.setBy
                                                                    ? `Von ${p.setBy} gesetzt — überlebt den Abgleich`
                                                                    : 'Von Hand gesetzt — überlebt den Abgleich'}>
                                                                    <Chip size="small" color="warning" variant="outlined"
                                                                        label="eingestellt" />
                                                                </Tooltip>
                                                                <Button size="small" color="inherit"
                                                                    disabled={savingPrice}
                                                                    onClick={() => releasePrice(p.model, p.tokenType)}>
                                                                    freigeben
                                                                </Button>
                                                            </>
                                                        ) : (
                                                            <Tooltip title={`Aus der Abrechnung gemessen · Stand ${new Date(p.updatedAt).toLocaleString('de-DE')}`}>
                                                                <Typography variant="caption" color="text.secondary"
                                                                    sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                                                    {tokens(p.tokens)} Token
                                                                </Typography>
                                                            </Tooltip>
                                                        )}
                                                    </Stack>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                            Noch keine Preise abgeleitet. Dafür braucht es den Admin-Schlüssel und
                            genügend Verbrauch — unter 50.000 Token je Modell und Art wird die Zahl
                            von der Rundung der Abrechnung bestimmt und deshalb verworfen. Bis dahin
                            rechnet die Schätzung mit hinterlegten Listenpreisen; von Hand eingetragene
                            Preise gehen ihnen vor.
                        </Alert>
                    )}

                    {/*
                      * Der Weg ohne Admin-Schlüssel: einen Preis von Hand anlegen. Ohne diese Zeile
                      * bliebe die Tabelle leer, und „einstellbar" wäre nur ein Versprechen für
                      * Häuser, die die Abrechnung abrufen können.
                      */}
                    <Divider sx={{ my: 1.5 }} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
                        sx={{ alignItems: { sm: 'center' } }}>
                        <TextField
                            size="small" label="Modell" placeholder="claude-opus-5"
                            value={newPrice.model}
                            onChange={(e) => setNewPrice({ ...newPrice, model: e.target.value })}
                            sx={{ minWidth: 200 }}
                        />
                        <TextField
                            select size="small" label="Token-Art"
                            value={newPrice.tokenType}
                            onChange={(e) => setNewPrice({ ...newPrice, tokenType: e.target.value })}
                            sx={{ minWidth: 200 }}
                        >
                            {TOKEN_TYPE_OPTIONS.map((t) => (
                                <MenuItem key={t} value={t}>{TOKEN_TYPE_LABELS[t] ?? t}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small" type="number" label="USD je Mio."
                            value={newPrice.value}
                            onChange={(e) => setNewPrice({ ...newPrice, value: e.target.value })}
                            slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
                            sx={{ width: 150 }}
                        />
                        <Button
                            variant="outlined" size="small"
                            disabled={savingPrice || !newPrice.model.trim() || !newPrice.value}
                            onClick={async () => {
                                await savePrice(newPrice.model.trim(), newPrice.tokenType, newPrice.value);
                                setNewPrice({ ...newPrice, model: '', value: '' });
                            }}
                        >
                            Preis setzen
                        </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        In <b>Dollar</b> je Million Token — derselben Einheit, in der der Anbieter
                        abrechnet. In Euro einzugeben wäre bequemer und falsch: der Kurs ist eine
                        eigene Einstellung, und eine Zahl mit zwei Annahmen darin lässt sich später
                        nicht mehr auseinandernehmen.
                    </Typography>
                </CardContent>
            </Card>

            <Alert severity="info" variant="outlined">
                Die Beträge sind eine <b>Schätzung</b> aus Listenpreisen und einem angenommenen
                Wechselkurs — als Größenordnung und als Notbremse. Sie ersetzen die Abrechnung des
                Anbieters nicht. Selbst betriebene Modelle werden mit 0 € geführt.
            </Alert>
        </Stack>
    );
};

/**
 * Eine Aufschlüsselung als Tabelle mit Anteilsbalken.
 *
 * Die Tabelle ist hier keine Zugabe: im Hellmodus liegen zwei der Serienfarben unter 3:1 Kontrast
 * zur Fläche, und dann verlangt die Zugänglichkeit einen Weg an dieselben Zahlen, der nicht über
 * Farbe führt.
 */
const ShareTable: React.FC<{
    rows: UsageSlice[];
    label: (name: string) => string;
    color?: (name: string) => string;
    accent?: string;
}> = ({ rows, label, color, accent }) => {
    const theme = useTheme();

    if (rows.length === 0) {
        return <Typography variant="body2" color="text.secondary">Noch nichts erfasst.</Typography>;
    }

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Posten</TableCell>
                    <TableCell align="right">Aufrufe</TableCell>
                    <TableCell align="right">ein</TableCell>
                    <TableCell align="right">aus</TableCell>
                    <TableCell align="right">Cache gel./gesch.</TableCell>
                    <TableCell align="right">Kosten</TableCell>
                    <TableCell sx={{ width: '22%' }}>Anteil</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {rows.map((r) => (
                    <TableRow key={r.name} hover>
                        <TableCell>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                {color && (
                                    <Box sx={{
                                        width: 10, height: 10, borderRadius: '2px',
                                        bgcolor: color(r.name), flexShrink: 0,
                                    }} />
                                )}
                                {label(r.name)}
                            </Stack>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {r.calls.toLocaleString('de-DE')}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {tokens(r.inputTokens)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            {tokens(r.outputTokens)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            <Tooltip title="Gelesen kostet ein Zehntel, geschrieben das Anderthalbfache.">
                                <span>{tokens(r.cachedTokens)} / {tokens(r.cacheWriteTokens)}</span>
                            </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                            {eur(r.estimatedEur)}
                        </TableCell>
                        <TableCell>
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                <Box sx={{
                                    flexGrow: 1, height: 6, borderRadius: 3,
                                    bgcolor: theme.palette.action.hover, overflow: 'hidden',
                                }}>
                                    <Box sx={{
                                        width: `${Math.max(1, r.share * 100)}%`, height: '100%',
                                        borderRadius: 3,
                                        bgcolor: color ? color(r.name) : (accent ?? theme.palette.primary.main),
                                    }} />
                                </Box>
                                <Typography variant="caption" color="text.secondary"
                                    sx={{ minWidth: 34, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                                    {Math.round(r.share * 100)} %
                                </Typography>
                            </Stack>
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

export default AiCostReport;
